import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { axiosInstance } from "../../../utils/axios";

import {
  Avatar,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper as MuiPaper,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";

import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";

// Import components
import FormTab from "./FormTab";
import FieldsTab from "./FieldsTab";
import RoutingTab from "./RoutingTab";
import RoutingFormsContextWrapper from "./RoutingFormsProvider";

// Styled components
const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function EditRoutingForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("form");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch routing form data from backend
  useEffect(() => {
    const fetchRoutingForm = async () => {
      try {
        setLoading(true);
        console.log("Fetching routing form with ID:", id);
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetRoutingForm($id: ID!) {
              getRoutingForm(id: $id) {
                _id
                name
                description
                status
                fields
                routes
                fallback
                emailSettings
                createdAt
                updatedAt
              }
            }
          `,
          variables: { id },
        });

        console.log("Raw API response:", response.data);

        const result =
          response.data?.data?.getRoutingForm || response.data?.getRoutingForm;

        if (result) {
          // Parse JSON fields if they are strings
          const formData = {
            ...result,
            fields:
              typeof result.fields === "string"
                ? JSON.parse(result.fields)
                : result.fields || [],
            routes:
              typeof result.routes === "string"
                ? JSON.parse(result.routes)
                : result.routes || [],
            fallback:
              typeof result.fallback === "string"
                ? JSON.parse(result.fallback)
                : result.fallback || {
                    type: "CUSTOM_PAGE",
                    value: "Thank you for your submission!",
                  },
            emailSettings:
              typeof result.emailSettings === "string"
                ? JSON.parse(result.emailSettings)
                : result.emailSettings || {},
          };
          setForm(formData);
          console.log("Loaded routing form:", formData);
        } else {
          setError(new Error("Form not found"));
        }
      } catch (err) {
        console.error("Error fetching routing form:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRoutingForm();
    }
  }, [id]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // If form is still loading
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          Loading form...
        </Typography>
      </Box>
    );
  }

  // If there was an error loading the form
  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={6}>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 600, mb: 2 }}>
          Error loading form: {error.message}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/calendar/routing-forms")}
          startIcon={<ArrowBackIcon />}
        >
          Back to Routing Forms
        </Button>
      </Box>
    );
  }

  // If no form data was found
  if (!form) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={6}>
        <Alert severity="warning" sx={{ width: "100%", maxWidth: 600, mb: 2 }}>
          Form not found or access denied.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/calendar/routing-forms")}
          startIcon={<ArrowBackIcon />}
        >
          Back to Routing Forms
        </Button>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={`Edit Routing Form: ${form?.name}`} />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Edit Routing Form
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <NavLink to="/calendar/dashboard">Dashboard</NavLink>
            <NavLink to="/calendar/routing-forms">Routing Forms</NavLink>
            <Typography>Edit Form</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="inherit"
              sx={{ mx: "0.5rem" }}
              component={NavLink}
              to="/calendar/routing-forms"
            >
              <ArrowBackIcon />
              Back
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12} md={3}>
          <Paper>
            <List>
              <ListItem
                button
                selected={activeTab === "form"}
                onClick={() => handleTabChange("form")}
                sx={{
                  borderLeft: activeTab === "form" ? "3px solid" : "none",
                  backgroundColor:
                    activeTab === "form" ? "rgba(0, 0, 0, 0.04)" : "inherit",
                  minHeight: "50px",
                }}
              >
                <ListItemText primary="Form" />
                {activeTab === "form" && (
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <ArrowBackIcon
                      sx={{
                        transform: "rotate(180deg)",
                        fontSize: "1.2rem",
                      }}
                    />
                  </ListItemIcon>
                )}
              </ListItem>

              <ListItem
                button
                selected={activeTab === "fields"}
                onClick={() => handleTabChange("fields")}
                sx={{
                  borderLeft: activeTab === "fields" ? "3px solid" : "none",
                  backgroundColor:
                    activeTab === "fields" ? "rgba(0, 0, 0, 0.04)" : "inherit",
                  minHeight: "50px",
                }}
              >
                <ListItemText primary="Fields" />
                {activeTab === "fields" && (
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <ArrowBackIcon
                      sx={{
                        transform: "rotate(180deg)",
                        fontSize: "1.2rem",
                      }}
                    />
                  </ListItemIcon>
                )}
              </ListItem>

              <ListItem
                button
                selected={activeTab === "routing"}
                onClick={() => handleTabChange("routing")}
                sx={{
                  borderLeft: activeTab === "routing" ? "3px solid" : "none",
                  backgroundColor:
                    activeTab === "routing" ? "rgba(0, 0, 0, 0.04)" : "inherit",
                  minHeight: "50px",
                }}
              >
                <ListItemText primary="Routing" />
                {activeTab === "routing" && (
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <ArrowBackIcon
                      sx={{
                        transform: "rotate(180deg)",
                        fontSize: "1.2rem",
                      }}
                    />
                  </ListItemIcon>
                )}
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          {activeTab === "form" && <FormTab form={form} setForm={setForm} />}
          {activeTab === "fields" && (
            <FieldsTab form={form} setForm={setForm} />
          )}
          {activeTab === "routing" && (
            <RoutingFormsContextWrapper>
              <RoutingTab form={form} setForm={setForm} />
            </RoutingFormsContextWrapper>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default EditRoutingForm;
